import { useSuspenseQuery } from '@tanstack/react-query';
import { createFileRoute, notFound } from '@tanstack/react-router';
import type { ComponentType } from 'react';

import { ArrowLeft } from '@repo/media/icons';

import { ButtonLink } from '@repo/components/button-link';
import { Heading } from '@repo/components/heading';

import { FeatureGuard } from '~/features/auth/feature-guard.tsx';
import { queryOrganizationById } from '~/services/authorization/authorization.ts';
import { organization } from '~/services/authorization/types.ts';

import { GuideDocument } from './-components/guide-document.tsx';

type Guide = {
  title: string;
  description: string;
  component: ComponentType<{ organizationId: string }>;
};

// Guides reachable from the API documentation page. Add a how-to (for example, one
// integration's call sequence) as another entry; it gets its own URL and header.
const guides: Record<string, Guide> = {
  onboarding: {
    title: 'Onboarding guide',
    description: 'Register the Northern Lights applications in your Microsoft Entra ID tenant so your users can sign in.',
    component: ({ organizationId }) => (
      <GuideDocument
        organizationId={organizationId}
        guide="onboarding"
        note="The consent link in this guide is prefilled for your Entra ID tenant. Share the PDF with your tenant administrator if that is not you."
      />
    ),
  },
  integration: {
    title: 'API integration guide',
    description: 'Set up a service principal in your own tenant for systems that call the Northern Lights API without a signed-in user.',
    component: ({ organizationId }) => (
      <GuideDocument
        organizationId={organizationId}
        guide="integration"
        note="For the developer setting up the integration and the organisation administrator who registers its service principal."
      />
    ),
  },
};

export const Route = createFileRoute('/organization/$org/guides/$guide')({
  loader: async ({ context: { queryClient }, params: { org, guide } }) => {
    if (!(guide in guides)) {
      throw notFound();
    }
    return queryClient.ensureQueryData(queryOrganizationById(org));
  },
  component: RouteComponent,
});

function RouteComponent() {
  const { org, guide: guideId } = Route.useParams();
  const organizationData = useSuspenseQuery(queryOrganizationById(org)).data;
  const guide = guides[guideId];
  if (!guide) {
    throw notFound();
  }

  return (
    <FeatureGuard features={organization('api-documentation:read')}>
      <div className="flex flex-col gap-6 w-full pb-6">
        <div className="flex w-full flex-wrap items-start justify-between gap-4">
          <div className="flex flex-col gap-1">
            <Heading className="text-2xl lg:text-3xl font-light">{guide.title}</Heading>
            <p>
              {guide.description}
              {' '}
              Prepared for
              {' '}
              {organizationData.name}
              .
            </p>
          </div>
          <ButtonLink
            to="/organization/$org/api-docs"
            params={{ org }}
            variant="soft"
            size="sm"
            className="w-fit"
            icon={{ component: <ArrowLeft size={18} /> }}
          >
            Back to API documentation
          </ButtonLink>
        </div>

        <guide.component organizationId={org} />
      </div>
    </FeatureGuard>
  );
}
